import { openDB } from "idb"

const DB_NAME = "jewlr-images-v2"
const STORE_NAME = "images"
const DB_VERSION = 1
const keyPrefix = "_jwl_"
const defaultDays = 1

const memoryCache = new Map() // Fallback when IndexedDB is unavailable. Data is lost on page unload.
let dbPromise = null

// Resolves to null on failure (e.g. IndexedDB disabled in private browsing)
// so callers can guard with a simple null-check instead of try/catch.
const openConnection = () => {
  if (typeof window === "undefined" || !window.indexedDB) {
    return Promise.resolve(null)
  }

  const attempt = openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      db.createObjectStore(STORE_NAME, { keyPath: "key" })
    },
  })
    .then((db) => {
      // iOS WebKit (Safari and Chrome-on-iOS) can close an open IndexedDB
      // connection out from under the page at any time (memory pressure,
      // backgrounding, idle eviction). When that happens, invalidate the
      // cached promise so the next call reopens a fresh connection instead
      // of reusing a dead one forever.
      db.onclose = () => {
        if (dbPromise === attempt) {
          dbPromise = null
        }
      }
      return db
    })
    .catch((e) => {
      console.error(
        "IndexedDBCache: failed to open DB, falling back to in-memory cache",
        e
      )
      if (dbPromise === attempt) {
        dbPromise = null
      }
      return null
    })

  return attempt
}

const getDb = () => {
  if (!dbPromise) dbPromise = openConnection()
  return dbPromise
}

const closingIndexedDbMessages = [
  "the database connection is closing", // Chrome, Safari, Firefox
  "can't start a transaction on a closed database", // Firefox alternate
]

const isClosingIndexedDbError = (e) =>
  !!e &&
  e.name === "InvalidStateError" &&
  typeof e.message === "string" &&
  closingIndexedDbMessages.some((msg) =>
    e.message?.toLowerCase().includes(msg.toLowerCase())
  )

// Runs operation(db) against the current connection. Returns null when
// IndexedDB is unavailable or the connection couldn't be recovered (caller
// should fall back to memoryCache), or { result } on success — wrapped so a
// legitimately falsy result isn't confused with "unavailable".
const withDb = async (operation) => {
  let currentDbPromise = getDb()
  let db = await currentDbPromise
  if (!db) return null

  let retry = 3

  while (retry > 0) {
    try {
      return { result: await operation(db) }
    } catch (e) {
      if (!isClosingIndexedDbError(e)) throw e

      if (retry === 0) {
        console.error("IndexedDBCache: retry after reconnect failed", e)
        return null
      }
      retry -= 1

      // Avoid a "reconnection storm": if another concurrent call already
      // started (or finished) reopening since we grabbed currentDbPromise,
      // reuse that attempt instead of opening yet another redundant
      // connection. The `|| !dbPromise` fallback covers the case where
      // `onclose` nulled dbPromise out from under us in the meantime.
      if (dbPromise === currentDbPromise || !dbPromise) {
        dbPromise = openConnection()
      }
      currentDbPromise = dbPromise
      db = await currentDbPromise
      if (!db) return null
    }
  }
}

const pkey = (key) => `${keyPrefix}${key}`

// TTL is stored in a separate lightweight "_meta" record so exist() and
// expiry checks never load the multi-megabyte image value into memory.
const metaKey = (key) => `${pkey(key)}_meta`

const ttlTimestamp = () => Date.now() + defaultDays * 24 * 3600 * 1000

const readMemoryEntry = (key) => {
  const entry = memoryCache.get(pkey(key))
  if (!entry) return null
  if (entry.ttl <= Date.now()) {
    memoryCache.delete(pkey(key))
    return null
  }
  return entry
}

const store = async (key, data) => {
  try {
    const outcome = await withDb((db) => {
      const tx = db.transaction(STORE_NAME, "readwrite")
      return Promise.all([
        tx.store.put({ key: pkey(key), value: data }),
        tx.store.put({ key: metaKey(key), ttl: ttlTimestamp() }),
        tx.done,
      ])
    })
    if (!outcome) {
      memoryCache.set(pkey(key), { ttl: ttlTimestamp(), value: data })
    }
  } catch (e) {
    console.error("IndexedDBCache store failed", e)
  }
}

const get = async (key) => {
  try {
    const outcome = await withDb(async (db) => {
      const meta = await db.get(STORE_NAME, metaKey(key))
      if (!meta || meta.ttl <= Date.now()) {
        if (meta) {
          const tx = db.transaction(STORE_NAME, "readwrite")
          await Promise.all([
            tx.store.delete(pkey(key)),
            tx.store.delete(metaKey(key)),
            tx.done,
          ])
        }
        return null
      }
      const entry = await db.get(STORE_NAME, pkey(key))
      return entry?.value ?? null
    })
    if (!outcome) {
      return readMemoryEntry(key)?.value ?? null
    }
    return outcome.result
  } catch (e) {
    console.error("IndexedDBCache get failed", e)
    return null
  }
}

const exist = async (key) => {
  try {
    const outcome = await withDb(async (db) => {
      const meta = await db.get(STORE_NAME, metaKey(key))
      return !!meta && meta.ttl > Date.now()
    })
    if (!outcome) {
      return !!readMemoryEntry(key)
    }
    return outcome.result
  } catch (e) {
    console.error("IndexedDBCache exist failed", e)
    return false
  }
}

const remove = async (key) => {
  try {
    const outcome = await withDb((db) => {
      const tx = db.transaction(STORE_NAME, "readwrite")
      return Promise.all([
        tx.store.delete(pkey(key)),
        tx.store.delete(metaKey(key)),
        tx.done,
      ])
    })
    if (!outcome) {
      memoryCache.delete(pkey(key))
    }
  } catch (e) {
    console.error("IndexedDBCache remove failed", e)
  }
}

// In Mobile Safari, read of a Blob/File stored in IndexedDB can returns empty
// Storing in ArrayBuffer rather than blob to avoid that bug
const storeBlob = async (key, blob) => {
  const buffer = await blob.arrayBuffer()
  await store(key, { buffer, type: blob.type })
}

const getBlob = async (key) => {
  const entry = await get(key)
  if (!entry) return null
  if (entry instanceof Blob) return entry
  if (entry.buffer) return new Blob([entry.buffer], { type: entry.type })
  return null
}

const IndexedDBCache = { exist, get, getBlob, remove, store, storeBlob }
export default IndexedDBCache
